// Owner-only Zoom-walkthrough switch. Renders ONLY for the real platform_admin.
// Intentionally near-invisible: a thin vertical sliver pinned to the right edge.
// The owner knows where it is; prospects on a Zoom share don't.
//
// Toggle ON:  picks the Pathway IRCC demo org, sets X-Demo-Org-Id (via api.js
//             interceptor), refreshes /auth/me so the backend's _attach_badge
//             returns the org's current `provisioned_by` value — honouring the
//             org's "Show firm name" toggle. Bounces off /admin so the platform
//             admin surface never flashes during a screen share.
// Toggle OFF: clears state, refreshes /auth/me, bounces off /org.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '@/lib/api';
import { useApp } from '@/lib/store';
import { cn } from '@/lib/utils';

const PATHWAY_NAME = 'Pathway IRCC Services';

export default function DemoPersonaSwitch() {
  const { user, setUser, demoPersona, setDemoPersona, clearDemoPersona } = useApp();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (user?.role !== 'platform_admin') return null;
  const on = demoPersona === 'org_admin';

  const refreshMe = async () => {
    try {
      const r = await api.me();
      setUser(r.data.user);
    } catch { /* token interceptor still has the header — backend will recover */ }
  };

  const enable = async () => {
    setBusy(true);
    try {
      const res = await api.adminOrgs();
      const orgs = res.data?.orgs || [];
      // Prefer the seeded Pathway IRCC demo org by name; never auto-pick a
      // paid org (those carry real customer data and look empty in demos).
      const target = orgs.find((o) => o.provisioned_by === PATHWAY_NAME && o.status === 'active')
        || orgs.find((o) => o.kind === 'demo' && o.status === 'active')
        || orgs.find((o) => o.name === PATHWAY_NAME && o.status === 'active');
      if (!target) return;
      setDemoPersona('org_admin', target.org_id, target.provisioned_by || target.name);
      await refreshMe();
      if (window.location.pathname.startsWith('/admin')) navigate('/org');
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      clearDemoPersona();
      await refreshMe();
      if (window.location.pathname.startsWith('/org')) navigate('/admin');
    } finally { setBusy(false); }
  };

  return (
    <button
      type="button"
      onClick={on ? disable : enable}
      disabled={busy}
      data-testid="demo-persona-toggle"
      aria-pressed={on}
      aria-label={on ? 'Exit demo persona' : 'Enter demo persona'}
      className={cn(
        'fixed right-0 top-1/2 z-50 hidden h-12 w-1.5 -translate-y-1/2 cursor-pointer rounded-l-full transition-colors md:block',
        on ? 'bg-indigo-500/70 hover:bg-indigo-500' : 'bg-slate-300/40 hover:bg-slate-400/70'
      )}
    />
  );
}
